import useModal from '@area2k/use-modal';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GAEvent } from '@/constants/gaevents';
import useAnalytics from '@/util/analytics';
import { isIOS, isWeb } from '@/util/platform';

import EntryScreen from './EntryScreen';
import { buildCreatePayload } from './api/buildCreatePayload';
import { createOrderV2Client } from './api/client';
import {
  BillingAccountV2,
  OrderTemplateDetail,
  SavedSnippet,
  SavedSnippetFieldKind,
} from './api/types';
import useCustomersDirectory from './hooks/useCustomersDirectory';
import useCertificates from './hooks/useCertificates';
import useLocations from './hooks/useLocations';
import useOrderTemplate from './hooks/useOrderTemplate';
import useOrderTemplatesList from './hooks/useOrderTemplatesList';
import usePaymentMethods from './hooks/usePaymentMethods';
import useRateQuote from './hooks/useRateQuote';
import useSavedSnippets from './hooks/useSavedSnippets';
import useShiftSupervisors from './hooks/useShiftSupervisors';
import useSkillCategories from './hooks/useSkillCategories';
import useSkills from './hooks/useSkills';
import useSurgeRates from './hooks/useSurgeRates';
import useUniforms from './hooks/useUniforms';
import useVerificationFlag from './hooks/useVerificationFlag';
import {
  initialOrderStateV2,
  defaultJobDraftV2,
  JobDraftV2,
  OrderActionsContext,
  OrderStateContext,
  OrderStateV2,
} from './context';
import WizardHeader from './components/WizardHeader';
import BrowseSavedModal from './components/modals/BrowseSavedModal';
import SaveSnippetModal from './components/modals/SaveSnippetModal';
import PostSubmitModal from './components/modals/PostSubmitModal';
import {
  ErrorBlock,
  FooterCancel,
  FooterGhost,
  FooterPrimary,
  StickyFooter,
  WizardBody,
  WizardShell,
} from './styles';
import InstructionsStep from './Steps/InstructionsStep';
import JobTypeStep from './Steps/JobTypeStep';
import PayRateStep from './Steps/PayRateStep';
import ReviewStep from './Steps/ReviewStep';
import ScheduleStep from './Steps/ScheduleStep';
import StaffingStep from './Steps/StaffingStep';
import {
  addNewJobDraftReducer,
  applyRateQuoteReducer,
  autoSeedCertificateDefaultsReducer,
  cancelNewJobDraftReducer,
} from './stateReducers';

const BASE_STEP_LABELS = [
  'Job type',
  'Schedule',
  'Staffing',
  'Instructions',
  'Submission',
];
// Markup billing accounts add a "Pay Rate" step right before Submission,
// mirroring the legacy CreateGigOrder flow.
const MARKUP_STEP_LABELS = [
  'Job type',
  'Schedule',
  'Staffing',
  'Instructions',
  'Pay Rate',
  'Submission',
];
const VIEW_ENTRY = 'entry';
const VIEW_WIZARD = 'wizard';
type ViewMode = typeof VIEW_ENTRY | typeof VIEW_WIZARD;

type SnippetApplyHandler = (body: string) => void;

type SnippetPanel =
  | {
      mode: 'browse';
      fieldKind: SavedSnippetFieldKind;
      title: string;
      onApply: SnippetApplyHandler;
    }
  | {
      mode: 'save';
      fieldKind: SavedSnippetFieldKind;
      title: string;
      currentBody: string;
    }
  | { mode: 'closed' };

const closedPanel: SnippetPanel = { mode: 'closed' };

const AgencyCreateOrderV2 = () => {
  const [state, setState] = useState<OrderStateV2>(initialOrderStateV2);
  const {
    billingAccounts,
    customers: directoryCustomers,
    refresh: refreshDirectory,
  } = useCustomersDirectory();
  const [viewMode, setViewMode] = useState<ViewMode>(VIEW_ENTRY);
  const [snippetPanel, setSnippetPanel] = useState<SnippetPanel>(closedPanel);
  const [browseSnippets, setBrowseSnippets] = useState<SavedSnippet[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [applyingSnippetId, setApplyingSnippetId] = useState<number | null>(
    null,
  );
  const [savingSnippet, setSavingSnippet] = useState(false);
  const [snippetError, setSnippetError] = useState<string | null>(null);

  const [skillSearch, setSkillSearch] = useState('');
  const billingAccountId = state.billingAccount?.id ?? null;
  const { templates } = useOrderTemplatesList(
    billingAccountId,
    state.orderType,
  );
  const { skills, skillsLoading, skillsError } = useSkills(
    billingAccountId,
    skillSearch,
  );
  const { skillCategories } = useSkillCategories(billingAccountId);
  const { paymentMethods, paymentMethodsLoading, paymentMethodsError } =
    usePaymentMethods(billingAccountId);
  const { uniforms, uniformsLoading, uniformsError } =
    useUniforms(billingAccountId);
  const { addressSnippets, checkInSnippets, refreshSnippetsForKind } =
    useSavedSnippets(billingAccountId);
  const { locations, refreshLocations } = useLocations(billingAccountId);
  const { shiftSupervisors, refreshShiftSupervisors } =
    useShiftSupervisors(billingAccountId);
  const { surgeRates } = useSurgeRates(
    billingAccountId,
    !!state.billingAccount?.enable_surge_rate,
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<{
    orderId: number | string;
    hireMessage: string | null;
  } | null>(null);

  const { logEvent } = useAnalytics();

  // Staffing step has an internal sub-step: when "Send Invitations" is the
  // strategy, the worker list is hidden behind a "Continue to Invite"
  // transition. LTA always starts on 'invite' since it has no choice card.
  const [staffingSubstep, setStaffingSubstep] = useState<'choice' | 'invite'>(
    'choice',
  );

  // Reset the staffing sub-step whenever the wizard navigates away from the
  // staffing step, so re-entering the step always starts at the choice cards.
  useEffect(() => {
    if (state.step !== 2) setStaffingSubstep('choice');
    else if (state.orderType === 'LTA') setStaffingSubstep('invite');
  }, [state.step, state.orderType]);

  // Tracks whether every job's pay rate meets its market minimum on the
  // PayRateStep. Only consulted when the markup-only Pay Rate step is shown.
  const [payRateValid, setPayRateValid] = useState(false);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'GIG' || typeParam === 'LTA') {
      setState((prev) =>
        prev.orderType === typeParam
          ? prev
          : {
              ...prev,
              orderType: typeParam,
              jobs:
                prev.jobs.length > 0
                  ? prev.jobs.map((job) => ({ ...job, job_type: typeParam }))
                  : [defaultJobDraftV2(typeParam)],
            },
      );
    }
  }, [searchParams]);

  const customerId = state.billingAccount?.customer_id ?? null;

  const job0 = state.jobs[0];
  const job0SkillId = job0?.skill_id ?? null;
  const job0AddressId = job0?.address_id ?? null;
  const job0HasRates = !!(job0 && (job0.pay_rate || job0.cost_rate));

  const actions = useMemo(
    () => ({
      setBillingAccount: (billingAccount: BillingAccountV2 | null) =>
        setState((prev) => ({
          ...prev,
          billingAccount,
          // Switching billing accounts invalidates any prior template prefill
          // since templates are billing-account scoped.
          sourceTemplateId: null,
          isPrefilled: false,
          jobs: prev.orderType ? [defaultJobDraftV2(prev.orderType)] : [],
        })),
      setOrderType: (orderType: 'GIG' | 'LTA' | null) =>
        setState((prev) => ({
          ...prev,
          orderType,
          jobs:
            prev.jobs.length > 0
              ? prev.jobs.map((job) => ({
                  ...job,
                  job_type: orderType || 'GIG',
                }))
              : orderType
                ? [defaultJobDraftV2(orderType)]
                : [],
        })),
      applyTemplate: (template: OrderTemplateDetail) => {
        // OpenAPI types `order_type` as a plain string; the live builder
        // only emits 'GIG' or 'LTA'. Narrow at the boundary.
        const orderType: 'GIG' | 'LTA' =
          template.order_type === 'LTA' ? 'LTA' : 'GIG';
        setState((prev) => ({
          ...prev,
          sourceTemplateId: template.template_id,
          orderType,
          isPrefilled: true,
          // Carry the template's QR check-in flag forward as the default; the
          // billing-account fetch will still gate visibility based on the
          // customer setting.
          verificationEnabled: !!template.verification_enabled,
          jobs: template.jobs.map((job) => ({
            ...defaultJobDraftV2(orderType),
            ...(job as JobDraftV2),
            shifts: [],
            job_type: orderType,
          })),
          step: 0,
        }));
      },
      clearTemplate: () =>
        setState((prev) => ({
          ...prev,
          sourceTemplateId: null,
          isPrefilled: false,
          jobs: prev.orderType ? [defaultJobDraftV2(prev.orderType)] : [],
        })),
      setStep: (step: number) => setState((prev) => ({ ...prev, step })),
      setJobs: (jobs: JobDraftV2[]) => setState((prev) => ({ ...prev, jobs })),
      addNewJobDraft: () => setState((prev) => addNewJobDraftReducer(prev)),
      cancelNewJobDraft: () =>
        setState((prev) => cancelNewJobDraftReducer(prev)),
      setVerificationEnabled: (enabled: boolean) =>
        setState((prev) => ({ ...prev, verificationEnabled: enabled })),
      setVerificationVisible: (visible: boolean, defaultEnabled: boolean) =>
        setState((prev) => ({
          ...prev,
          verificationVisible: visible,
          // Only seed the default when the field becomes visible and is not
          // already prefilled from a template; otherwise keep the user's
          // current selection.
          verificationEnabled: prev.isPrefilled
            ? prev.verificationEnabled
            : visible
              ? defaultEnabled
              : false,
        })),
      // Re-checks the prefill / already-picked guards inside the
      // setState callback to avoid races with template apply or user
      // edits. Called by useCertificates after the customer's
      // pre-attached defaults come back. The reducer logic lives in
      // `./stateReducers` so it can be unit-tested independently of any
      // hook implementation.
      autoSeedCertificateDefaults: (defaultIds: number[]) =>
        setState((prev) =>
          autoSeedCertificateDefaultsReducer(prev, defaultIds),
        ),
      // Re-checks the "no rates set yet" guard inside the setState
      // callback so a tenant admin's manual edits aren't clobbered by
      // a background refetch. Called by useRateQuote.
      applyRateQuote: (pay: number, cost: number) =>
        setState((prev) => applyRateQuoteReducer(prev, pay, cost)),
    }),
    [],
  );

  useVerificationFlag(customerId, actions.setVerificationVisible);
  const { certificateOptions } = useCertificates(
    billingAccountId,
    state.isPrefilled,
    actions.autoSeedCertificateDefaults,
  );
  useRateQuote(
    billingAccountId,
    job0SkillId,
    job0AddressId,
    job0HasRates,
    actions.applyRateQuote,
  );

  const [showPostSubmitModal, hidePostSubmitModal] = useModal(
    () => (
      <PostSubmitModal
        orderId={orderData!.orderId}
        hireMessage={orderData!.hireMessage}
        hideModal={() => {
          hidePostSubmitModal();
          actions.clearTemplate();
          actions.setStep(0);
          setViewMode(VIEW_ENTRY);
          setOrderData(null);
        }}
      />
    ),
    [orderData, actions],
  );

  useEffect(() => {
    if (orderData) showPostSubmitModal();
  }, [orderData, showPostSubmitModal]);

  const { fetchTemplate } = useOrderTemplate();
  const onSelectTemplate = async (templateId: number) => {
    const template = await fetchTemplate(templateId);
    const templateAccount =
      billingAccounts.find(
        (account) => account.id === template.billing_account_id,
      ) || null;
    actions.setBillingAccount(templateAccount);
    actions.applyTemplate(template);
    setViewMode(VIEW_WIZARD);
  };

  const closeSnippetPanel = () => {
    setSnippetPanel(closedPanel);
    setSnippetError(null);
  };

  const openBrowseSaved = async (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    onApply: SnippetApplyHandler,
  ) => {
    if (!state.billingAccount) return;
    setSnippetError(null);
    setSnippetPanel({ mode: 'browse', fieldKind, title, onApply });
    setBrowseLoading(true);
    try {
      const snippets = await createOrderV2Client.listSavedSnippets(
        state.billingAccount.id,
        fieldKind,
      );
      setBrowseSnippets(snippets);
    } catch (error) {
      setSnippetError(
        (error as Error).message || 'Failed to load saved templates',
      );
    } finally {
      setBrowseLoading(false);
    }
  };

  const openSaveSnippet = (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    currentBody: string,
  ) => {
    setSnippetError(null);
    setSnippetPanel({ mode: 'save', fieldKind, title, currentBody });
  };

  const handleApplySnippet = async (snippet: SavedSnippet) => {
    if (snippetPanel.mode !== 'browse') return;
    setApplyingSnippetId(snippet.id);
    try {
      await createOrderV2Client.applySavedSnippet(snippet.id);
      snippetPanel.onApply(snippet.body);
      // Refresh local cache so the used_count badge reflects the new value
      // the next time the user opens the modal.
      if (state.billingAccount) {
        const refreshed = await createOrderV2Client.listSavedSnippets(
          state.billingAccount.id,
          snippetPanel.fieldKind,
        );
        setBrowseSnippets(refreshed);
      }
      closeSnippetPanel();
    } catch (error) {
      setSnippetError((error as Error).message || 'Failed to apply template');
    } finally {
      setApplyingSnippetId(null);
    }
  };

  const handleSaveSnippet = async ({
    name,
    body,
  }: {
    name: string;
    body: string;
  }) => {
    if (snippetPanel.mode !== 'save') return;
    if (!state.billingAccount) return;
    setSavingSnippet(true);
    setSnippetError(null);
    try {
      await createOrderV2Client.createSavedSnippet(state.billingAccount.id, {
        field_kind: snippetPanel.fieldKind,
        title: name,
        body,
      });
      await refreshSnippetsForKind(snippetPanel.fieldKind);
      closeSnippetPanel();
    } catch (error) {
      setSnippetError((error as Error).message || 'Failed to save template');
    } finally {
      setSavingSnippet(false);
    }
  };

  const isMarkup = state.billingAccount?.rate_type === 'MARKUP';
  const stepLabels = isMarkup ? MARKUP_STEP_LABELS : BASE_STEP_LABELS;
  const stepCount = stepLabels.length;
  // Pay Rate is the second-to-last step on markup billing accounts, slotted
  // between Instructions (step 3) and the Review/Submission step.
  const payRateStepIndex = isMarkup ? 4 : -1;
  const reviewStepIndex = stepCount - 1;

  const renderStep = () => {
    const jobs =
      state.jobs.length > 0
        ? state.jobs
        : [defaultJobDraftV2(state.orderType || 'GIG')];
    if (state.step === payRateStepIndex) {
      return (
        <PayRateStep
          jobs={jobs}
          onUpdateJobs={actions.setJobs}
          skills={skills}
          billingAccount={state.billingAccount}
          onValidityChange={setPayRateValid}
        />
      );
    }
    switch (state.step) {
      case 0:
        return (
          <JobTypeStep
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            skills={skills}
            skillsLoading={skillsLoading}
            skillsError={skillsError}
            categories={skillCategories}
            onSkillSearchChange={setSkillSearch}
            locations={locations}
            onCreateLocation={async (payload) => {
              if (!state.billingAccount) return null;
              const created = await createOrderV2Client.createLocation(
                state.billingAccount.id,
                payload,
              );
              await refreshLocations();
              return created;
            }}
            supervisors={shiftSupervisors}
            onCreateSupervisor={async (payload) => {
              if (!state.billingAccount) return null;
              const created = await createOrderV2Client.createShiftSupervisor(
                state.billingAccount.id,
                payload,
              );
              await refreshShiftSupervisors();
              return created;
            }}
            onBrowseSaved={openBrowseSaved}
            onSaveSnippet={openSaveSnippet}
            addressSnippets={addressSnippets}
            checkInSnippets={checkInSnippets}
          />
        );
      case 1:
        return (
          <ScheduleStep
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            skills={skills}
            orderType={state.orderType}
            billingAccount={state.billingAccount}
            surgeRates={surgeRates}
          />
        );
      case 2:
        return (
          <StaffingStep
            orderType={state.orderType}
            billingAccount={state.billingAccount}
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            skills={skills}
            substep={staffingSubstep}
            certificateOptions={certificateOptions}
          />
        );
      case 3:
        return (
          <InstructionsStep
            jobs={jobs}
            onUpdateJobs={actions.setJobs}
            uniforms={uniforms}
            uniformsLoading={uniformsLoading}
            uniformsError={uniformsError}
            onBrowseSaved={openBrowseSaved}
            onSaveSnippet={openSaveSnippet}
          />
        );
      default:
        return (
          <ReviewStep
            orderType={state.orderType}
            jobs={jobs}
            skills={skills}
            paymentMethods={paymentMethods}
            paymentMethodsLoading={paymentMethodsLoading}
            paymentMethodsError={paymentMethodsError}
            billingAccountName={state.billingAccount?.name || null}
            billingAccount={state.billingAccount}
            onUpdateJobs={actions.setJobs}
            onAddSkill={() => {
              // Mirrors legacy CreateGigOrder: starting a new skill must
              // begin a fresh draft so the wizard doesn't overwrite the
              // already-completed job(s). The fresh draft lives at
              // jobs[0] (where every step reads/writes); previously
              // completed jobs shift to indices 1+.
              actions.addNewJobDraft();
              actions.setStep(0);
            }}
            surgeRates={surgeRates}
          />
        );
    }
  };

  const submitOrder = async () => {
    const payload = buildCreatePayload(state, locations, surgeRates);
    if (!payload.account_id) return;
    setSubmitLoading(true);
    setSubmitError(null);
    const startTime = Date.now();
    try {
      // Mirrors legacy CreateGigOrder: emit a funnel event when the user
      // submits an order that includes a certificate/badge selection.
      if (
        (payload.jobs?.[0] as { certificates?: number[] } | undefined)
          ?.certificates?.length
      ) {
        logEvent(GAEvent.BadgeAddedOnJobCreation);
      }

      const response = await createOrderV2Client.createOrder(payload);
      if (!response?.success) {
        const message = response?.errors?.[0] || 'Failed to place order';
        setSubmitError(message);
        return;
      }
      const orderId = response?.data?.order?.id;
      if (orderId == null) {
        setSubmitError('Order placed but response is missing order id');
        return;
      }
      const hiredCount = response?.data?.workers_hired || 0;
      // Mirrors legacy: log the final-step submission funnel event tagged by
      // billing rate type (markup vs static) and order type (gig vs lta).
      const isMarkupAccount = state.billingAccount?.rate_type === 'MARKUP';
      const isLtaOrder = state.orderType === 'LTA';
      const stepEvent = isMarkupAccount
        ? isLtaOrder
          ? GAEvent.StepSixMarkupLta
          : GAEvent.StepSixMarkupGig
        : isLtaOrder
          ? GAEvent.StepFiveStaticLta
          : GAEvent.StepFiveStaticGig;
      const stepEventValue = isMarkupAccount
        ? state.billingAccount?.id
        : (orderId as number | string);
      logEvent(stepEvent, stepEventValue, {
        time_spent: (Date.now() - startTime) / 1000,
        platform: isWeb() ? 'web' : isIOS() ? 'ios' : 'android',
      });

      setOrderData({
        orderId,
        hireMessage:
          hiredCount > 0
            ? `${hiredCount} worker${hiredCount === 1 ? '' : 's'} hired.`
            : null,
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to place order';
      setSubmitError(message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const currentStrategy = state.jobs[0]?.staffing_strategy ?? null;
  const isStaffingStep = state.step === 2;
  const isLta = state.orderType === 'LTA';
  // GIG users must pick a strategy before advancing past the choice cards.
  // LTA implicitly uses Send Invitations, so it never blocks here.
  const showContinueToInvite =
    isStaffingStep &&
    !isLta &&
    staffingSubstep === 'choice' &&
    currentStrategy === 'SEND_INVITATIONS';

  const canAdvance = () => {
    if (state.step === 0) {
      const job = state.jobs[0];
      return (
        !!job &&
        job.skill_id != null &&
        job.address_id != null &&
        job.contact_id != null
      );
    }
    if (state.step === 1) return (state.jobs[0]?.shifts?.length || 0) > 0;
    if (state.step === 3) return !!state.jobs[0]?.uniform_id;
    if (isStaffingStep && !isLta && staffingSubstep === 'choice') {
      return currentStrategy !== null;
    }
    if (state.step === payRateStepIndex) return payRateValid;
    return true;
  };

  const handleBack = () => {
    if (isStaffingStep && staffingSubstep === 'invite' && !isLta) {
      setStaffingSubstep('choice');
      return;
    }
    actions.setStep(Math.max(0, state.step - 1));
  };

  const handleContinue = () => {
    if (showContinueToInvite) {
      setStaffingSubstep('invite');
      return;
    }
    actions.setStep(Math.min(stepCount - 1, state.step + 1));
  };

  return (
    <OrderStateContext.Provider value={state}>
      <OrderActionsContext.Provider value={actions}>
        {viewMode === VIEW_ENTRY ? (
          <EntryScreen
            billingAccounts={billingAccounts}
            directoryCustomers={directoryCustomers}
            onRefreshDirectory={refreshDirectory}
            templates={templates}
            selectedBillingAccountId={state.billingAccount?.id || null}
            onSelectBillingAccount={(account) => {
              actions.setBillingAccount(account);
            }}
            onPickTemplate={onSelectTemplate}
            onStartOrder={() => {
              actions.clearTemplate();
              actions.setStep(0);
              const orderType = state.orderType || 'GIG';
              if (!state.orderType) {
                actions.setOrderType(orderType);
              }
              actions.setJobs([defaultJobDraftV2(orderType)]);
              setViewMode(VIEW_WIZARD);
            }}
          />
        ) : null}

        {viewMode === VIEW_WIZARD ? (
          <WizardShell>
            <WizardHeader
              title={`Adding a new ${state.orderType === 'LTA' ? 'LTA' : 'Gig'} job`}
              steps={stepLabels}
              currentStep={state.step}
              onClose={() => {
                actions.clearTemplate();
                actions.setStep(0);
                setViewMode(VIEW_ENTRY);
              }}
            />
            <WizardBody>{renderStep()}</WizardBody>
            {submitError ? <ErrorBlock>{submitError}</ErrorBlock> : null}
            <StickyFooter>
              {state.step > 0 ? (
                <FooterGhost
                  type="button"
                  disabled={submitLoading}
                  onClick={handleBack}
                >
                  {isStaffingStep && staffingSubstep === 'invite' && !isLta
                    ? 'Back'
                    : 'Go back'}
                </FooterGhost>
              ) : state.jobs.length > 1 ? (
                // Step 0 with an in-progress additional skill: bail out
                // of the new draft and return to review, instead of
                // wiping the whole order back to the entry screen.
                <FooterGhost
                  type="button"
                  disabled={submitLoading}
                  onClick={() => {
                    actions.cancelNewJobDraft();
                    actions.setStep(reviewStepIndex);
                  }}
                >
                  Go back
                </FooterGhost>
              ) : (
                <FooterCancel
                  type="button"
                  onClick={() => {
                    actions.clearTemplate();
                    actions.setStep(0);
                    setViewMode(VIEW_ENTRY);
                  }}
                >
                  Cancel
                </FooterCancel>
              )}
              {state.step === reviewStepIndex ? (
                <FooterPrimary
                  type="button"
                  disabled={submitLoading}
                  onClick={submitOrder}
                >
                  {submitLoading ? 'Placing order...' : 'Place new order'}
                </FooterPrimary>
              ) : (
                <FooterPrimary
                  type="button"
                  disabled={!canAdvance() || submitLoading}
                  onClick={handleContinue}
                >
                  {showContinueToInvite ? 'Continue to Invite' : 'Continue'}
                </FooterPrimary>
              )}
            </StickyFooter>

            <BrowseSavedModal
              isOpen={snippetPanel.mode === 'browse'}
              title={snippetPanel.mode === 'browse' ? snippetPanel.title : ''}
              snippets={browseSnippets}
              loading={browseLoading}
              applying={applyingSnippetId !== null}
              onCancel={closeSnippetPanel}
              onApply={handleApplySnippet}
            />

            <SaveSnippetModal
              isOpen={snippetPanel.mode === 'save'}
              title={snippetPanel.mode === 'save' ? snippetPanel.title : ''}
              initialBody={
                snippetPanel.mode === 'save' ? snippetPanel.currentBody : ''
              }
              saving={savingSnippet}
              errorMessage={snippetError}
              onCancel={closeSnippetPanel}
              onSave={handleSaveSnippet}
            />

            {snippetError && snippetPanel.mode === 'closed' ? (
              <ErrorBlock>{snippetError}</ErrorBlock>
            ) : null}
          </WizardShell>
        ) : (
          <>
            <BrowseSavedModal
              isOpen={snippetPanel.mode === 'browse'}
              title={snippetPanel.mode === 'browse' ? snippetPanel.title : ''}
              snippets={browseSnippets}
              loading={browseLoading}
              applying={applyingSnippetId !== null}
              onCancel={closeSnippetPanel}
              onApply={handleApplySnippet}
            />
            <SaveSnippetModal
              isOpen={snippetPanel.mode === 'save'}
              title={snippetPanel.mode === 'save' ? snippetPanel.title : ''}
              initialBody={
                snippetPanel.mode === 'save' ? snippetPanel.currentBody : ''
              }
              saving={savingSnippet}
              errorMessage={snippetError}
              onCancel={closeSnippetPanel}
              onSave={handleSaveSnippet}
            />
          </>
        )}
      </OrderActionsContext.Provider>
    </OrderStateContext.Provider>
  );
};

export default AgencyCreateOrderV2;
